// @flow
import invariant from 'invariant';
import * as React from 'react';
import { connect } from 'react-redux';
import { moveArrayItem } from 'utils/functional';
import Flex from 'components/Flex';
import Box from 'components/Box';
import { BodySmall } from 'components/Text';
import Icon from 'components/Icon';
import Video from 'components/Video';
import styled from 'styled-components';
import type { ImageData, ImageWithCaption } from 'api/shared';
import { imageUrl } from 'utils/filestack';
import { getRgbaString, retinaBackgroundImage } from 'utils/style';
import Button, { PrimaryButton } from 'components/Button';
import Container from 'components/Container';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { colors, transition, mediaRetina } from '../theme';
import { showModal } from '../redux/main/actions/modal';

const AddButton = styled(Button)`
  position: relative;
  & > * {
    position: relative;
    border: 1px dashed ${colors.silver};
    color: ${colors.waterBlue};
    border-radius: 4px;
    transition: all ${transition};
    @media ${mediaRetina} {
      border-width: 1.5px;
    }
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 6px;
    opacity: 0;
    background: ${colors.platinum};
    transition: all ${transition};
  }
  &:hover,
  &:focus {
    & > * {
      border-color: ${colors.niceBlue};
      color: ${colors.niceBlue};
    }
    &::before {
      opacity: 1;
    }
  }
`;

const ImageContainer = styled.div.attrs({ role: 'button' })`
  position: relative;
  width: 150px;
  height: 150px;
  background-color: ${colors.battleshipGrey};
  ${props =>
    retinaBackgroundImage(
      imageUrl({
        handle: props.handle,
        transform: `resize=width:150,height:150,fit:crop,align:center`
      }),
      {
        filename2x: imageUrl({
          handle: props.handle,
          transform: `resize=width:300,height:300,fit:crop,align:center`
        })
      }
    )}
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  cursor: move;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  z-index: 20;
  top: -13px;
  right: -13px;
  width: 25px;
  height: 25px;
  background-color: ${colors.waterBlue};
  background-image: url('/static/images/icons/trashcan-white.svg');
  background-size: 9px 10px;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease-in;
  &:hover {
    background-color: ${colors.turtleGreen};
  }
`;

const EditOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  opacity: 0;
  background: rgba(${getRgbaString('black', 0.5)});
  transition: all ${transition};

  &:hover {
    opacity: 1;
  }

  * {
    position: relative;
  }
`;

const SortableImage = SortableElement(
  ({ image, imageIndex, removeImage, editImage, qaId }) => {
    const removeHandler = () => {
      removeImage(imageIndex);
    };
    const editHandler = () => {
      editImage(imageIndex);
    };
    return (
      <Container m={11}>
        <ImageContainer
          data-qa-id={`${qaId}-uploaded-image`}
          handle={image.handle}
        >
          {image.type && image.type === 'VIDEO' && (
            <Video url={image.videoUrl} noPlayer fill smallButton />
          )}
          <EditOverlay>
            <PrimaryButton
              data-qa-id={`${qaId}-edit-image`}
              onClick={editHandler}
              size="small"
            >
              {getText('Edit')}
            </PrimaryButton>
          </EditOverlay>
          <DeleteButton
            title={getText('Remove')}
            data-qa-id={`${qaId}-remove-image`}
            onClick={removeHandler}
          />
        </ImageContainer>
      </Container>
    );
  }
);

const SortableGrid = SortableContainer(
  ({ values, removeImage, editImage, qaId, children }) => (
    <Flex
      flexDirection={['column', 'row', null, null]}
      flexWrap={['nowrap', 'wrap', null, null]}
      m={-11}
    >
      {values.map((image, index) => (
        <SortableImage
          image={image}
          removeImage={removeImage}
          editImage={editImage}
          qaId={qaId}
          imageIndex={index}
          index={index}
          key={image.fileId}
        />
      ))}
      {children}
    </Flex>
  )
);

type OwnProps = {
  values: ?Array<ImageWithCaption>,
  onChange: (Array<ImageWithCaption>) => void,
  allowVideo?: boolean,
  id?: string,
  'data-qa-id': string
};

type Props = {
  ...$Exact<OwnProps>,
  showModal: typeof showModal
};

class MultiImagePicker extends React.Component<Props> {
  addImage = () => {
    this.props.showModal('ADD_OR_EDIT_IMAGE', {
      allowVideo: this.props.allowVideo,
      saveImage: ({
        image,
        altText,
        caption,
        videoUrl,
        type = 'IMAGE'
      }: {
        image?: ImageData,
        altText?: string,
        caption?: string,
        videoUrl?: string,
        type?: string
      }) => {
        this.props.onChange([
          ...(this.props.values || []),
          { ...image, altText, caption, videoUrl, type }
        ]);
      }
    });
  };

  editImage = (index: number) => {
    const { values } = this.props;
    invariant(values, 'Missing image to edit');
    const {
      altText: currentAltText,
      caption: currentCaption,
      videoUrl: currentVideoUrl,
      type: currentType,
      ...currentImage
    } = values[index];

    this.props.showModal('ADD_OR_EDIT_IMAGE', {
      image: currentImage,
      altText: currentAltText,
      caption: currentCaption,
      videoUrl: currentVideoUrl,
      type: currentType,
      allowVideo: this.props.allowVideo,
      saveImage: ({
        image,
        altText,
        caption,
        videoUrl,
        type = 'IMAGE'
      }: {
        image?: ImageData,
        altText?: string,
        caption?: string,
        videoUrl?: string,
        type?: string
      }) => {
        const newValues = [...values];
        newValues[index] = { ...image, altText, caption, videoUrl, type };
        this.props.onChange(newValues);
      }
    });
  };

  removeImage = (index: number) => {
    invariant(this.props.values, 'Missing image to remove');
    const values = [...this.props.values];
    values.splice(index, 1);
    this.props.onChange(values);
  };

  sortHandler = ({
    oldIndex,
    newIndex
  }: {
    oldIndex: number,
    newIndex: number
  }) => {
    this.props.onChange(moveArrayItem(this.props.values, oldIndex, newIndex));
  };

  render() {
    const qaId = this.props['data-qa-id'];
    const values = this.props.values || [];

    return (
      <>
        <SortableGrid
          values={values}
          removeImage={this.removeImage}
          editImage={this.editImage}
          qaId={qaId}
          axis="xy"
          onSortEnd={this.sortHandler}
        >
          {(!values || values.length === 0) && (
            <Button
              onClick={this.addImage}
              tabIndex={-1}
              aria-label={
                this.props.allowVideo
                  ? getText('Add Image or Video')
                  : getText('Add Image')
              }
            >
              <Box
                position="relative"
                borderRadius="8px"
                bg={colors.platinum}
                width={150}
                height={150}
                m={11}
              >
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                >
                  <Icon name="image" width={45} fill={colors.aluminum} />
                </Box>
              </Box>
            </Button>
          )}
          <AddButton
            data-qa-id={`${qaId}-add-image`}
            onClick={this.addImage}
            borderRadius="4px"
            p={10}
            m={11}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              width={130}
              height={130}
            >
              <Box mt={16}>
                <Icon
                  width={23}
                  height={19}
                  fill="currentColor"
                  name="upload"
                />
              </Box>
              <BodySmall>
                {this.props.allowVideo
                  ? getText('Add Image or Video')
                  : getText('Add Image')}
              </BodySmall>
            </Box>
          </AddButton>
        </SortableGrid>
      </>
    );
  }
}

export default connect<Props, OwnProps, _, _, _, _>(
  null,
  { showModal }
)(MultiImagePicker);
